import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  Check as CheckIcon,
  Close as XIcon,
  AccessTime as ClockIcon,
  Person as UserIcon,
} from "@mui/icons-material";

interface LeaveRequest {
  id: number;
  employeeName: string;
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  status: "Pending" | "Approved" | "Rejected";
  requestDate: string;
  balance: number;
}

const LeaveRequestsTab: React.FC = () => {
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState("");

  const [requests, setRequests] = useState<LeaveRequest[]>([
    {
      id: 1,
      employeeName: "Sarah Johnson",
      employeeId: "EMP001",
      leaveType: "Vacation",
      startDate: "2025-01-10",
      endDate: "2025-01-15",
      days: 5,
      reason: "Family vacation to Europe",
      status: "Pending",
      requestDate: "2024-12-20",
      balance: 15,
    },
    {
      id: 2,
      employeeName: "Mike Chen",
      employeeId: "EMP002",
      leaveType: "Sick Leave",
      startDate: "2024-12-30",
      endDate: "2025-01-02",
      days: 3,
      reason: "Medical appointment and recovery",
      status: "Pending",
      requestDate: "2024-12-28",
      balance: 8,
    },
    {
      id: 3,
      employeeName: "Emily Davis",
      employeeId: "EMP003",
      leaveType: "Personal",
      startDate: "2025-01-08",
      endDate: "2025-01-08",
      days: 1,
      reason: "Moving to new apartment",
      status: "Pending",
      requestDate: "2024-12-25",
      balance: 3,
    },
    {
      id: 4,
      employeeName: "James Wilson",
      employeeId: "EMP004",
      leaveType: "Vacation",
      startDate: "2025-02-14",
      endDate: "2025-02-21",
      days: 7,
      reason: "Winter ski trip with family",
      status: "Pending",
      requestDate: "2024-12-22",
      balance: 20,
    },
    {
      id: 5,
      employeeName: "Lisa Brown",
      employeeId: "EMP005",
      leaveType: "Maternity",
      startDate: "2025-03-01",
      endDate: "2025-05-30",
      days: 60,
      reason: "Maternity leave for new baby",
      status: "Pending",
      requestDate: "2024-12-20",
      balance: 60,
    },
    {
      id: 6,
      employeeName: "David Kim",
      employeeId: "EMP006",
      leaveType: "Emergency",
      startDate: "2024-12-29",
      endDate: "2024-12-30",
      days: 2,
      reason: "Family emergency - father hospitalized",
      status: "Pending",
      requestDate: "2024-12-28",
      balance: 5,
    },
    {
      id: 7,
      employeeName: "Anna Rodriguez",
      employeeId: "EMP007",
      leaveType: "Vacation",
      startDate: "2025-04-15",
      endDate: "2025-04-22",
      days: 6,
      reason: "Spring break vacation",
      status: "Pending",
      requestDate: "2024-12-26",
      balance: 18,
    },
    {
      id: 8,
      employeeName: "Tom Anderson",
      employeeId: "EMP008",
      leaveType: "Sick Leave",
      startDate: "2025-01-03",
      endDate: "2025-01-04",
      days: 2,
      reason: "Flu symptoms and recovery",
      status: "Pending",
      requestDate: "2024-12-30",
      balance: 10,
    },
  ]);

  const pendingCount = requests.filter(req => req.status === "Pending").length;

  const handleApprove = (id: number) => {
    setRequests(prev =>
      prev.map(req =>
        req.id === id ? { ...req, status: "Approved" as const } : req
      )
    );
    setSuccessMessage("Leave request approved successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReject = (id: number) => {
    setRequests(prev =>
      prev.map(req =>
        req.id === id ? { ...req, status: "Rejected" as const } : req
      )
    );
    setSuccessMessage("Leave request rejected.");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getLeaveTypeColor = (leaveType: string) => {
    switch (leaveType) {
      case "Vacation":
        return "primary";
      case "Sick Leave":
        return "error";
      case "Personal":
        return "info";
      case "Maternity":
        return "secondary";
      case "Emergency":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Pending":
        return <ClockIcon />;
      case "Approved":
        return <CheckIcon />;
      case "Rejected":
        return <XIcon />;
      default:
        return <ClockIcon />;
    }
  };

  return (
    <Box>
      {/* Header with Stats */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <CalendarIcon color="primary" sx={{ mr: 1 }} />
          Leave Requests
          {pendingCount > 0 && (
            <Chip
              label={pendingCount}
              color="error"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pendingCount} leave requests pending approval
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Requests Grid */}
      <Grid container spacing={2}>
        {requests.map((request) => (
          <Grid item xs={12} md={6} lg={4} key={request.id}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.shadows[2],
                border: request.status === "Pending" ? `2px solid ${theme.palette.warning.main}` :
                        request.status === "Approved" ? `2px solid ${theme.palette.success.main}` : "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                backgroundColor: request.status === "Approved" ? alpha(theme.palette.success.main, 0.05) :
                               request.status === "Rejected" ? alpha(theme.palette.error.main, 0.05) :
                               alpha(theme.palette.warning.main, 0.05),
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <UserIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {request.employeeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {request.employeeId}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    icon={getStatusIcon(request.status)}
                    label={request.status}
                    color={getStatusColor(request.status) as any}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={request.leaveType}
                    color={getLeaveTypeColor(request.leaveType) as any}
                    variant="filled"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Period:</strong> {request.startDate} to {request.endDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Duration:</strong> {request.days} day{request.days > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Remaining Balance:</strong> {request.balance} days
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Requested:</strong> {request.requestDate}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Reason:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
                    "{request.reason}"
                  </Typography>
                </Box>

                {request.status === "Pending" && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={() => handleApprove(request.id)}
                      fullWidth
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<XIcon />}
                      onClick={() => handleReject(request.id)}
                      fullWidth
                    >
                      Reject
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {requests.length === 0 && (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <CalendarIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No leave requests found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Leave requests will appear here when submitted by employees.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default LeaveRequestsTab;